/**
 * Сповіщення: хто подав рапорт на звільнення через бота.
 *
 * Кожен рапорт показується один раз — і ще раз після кожної зміни в боті. Щойно список прийшов, сторінка
 * підтверджує його серверу — тож після оновлення сторінки чи входу з іншого
 * пристрою те саме вже не з'явиться. Висить, доки не закриють: тост за три
 * секунди зникав би раніше, ніж його встигнуть прочитати.
 *
 * Бот пише в базу окремим процесом, повз сервер, тож подією в каналі рапорт
 * не приходить — сторінка сама питає про нові раз на півхвилини і щойно
 * вкладку знову відкрили. Поки картка висить, нові рапорти дописуються в неї.
 */

import { shortName, type IsoDate } from '@kancelaria/shared';
import { useEffect, useRef, useState, type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../api/client.js';
import { useData } from '../store/data.js';
import { CloseIcon } from './ui.js';

interface NewsReport {
  personId: string;
  date: IsoDate;
  kind: 'leave' | 'duty';
  createdAt: string;
  /** Людина змінила вже поданий рапорт. */
  changed: boolean;
}

/** Більше рядків не вміщаємо: решту видно в таблиці «Звільнення о/с». */
const SHOWN = 6;

/** Як часто питати про нові рапорти. */
const POLL_MS = 30_000;

/** Скільки триває анімація зникнення — як у решти тостів. */
const EXIT_MS = 220;

/** «1 рапорт», «3 рапорти», «7 рапортів». */
function reportsText(n: number): string {
  const d = n % 10;
  const h = n % 100;
  const word = d === 1 && h !== 11 ? 'рапорт' : d >= 2 && d <= 4 && (h < 12 || h > 14) ? 'рапорти' : 'рапортів';
  return `${n} ${word}`;
}

const dayMonth = (d: IsoDate): string => `${d.slice(8)}.${d.slice(5, 7)}`;

/** Один рядок на людину й дату: зміна рапорту замінює попередній рядок. */
const keyOf = (r: NewsReport): string => `${r.personId}|${r.date}`;

export function BotReportsNotice(): ReactNode {
  const orgId = useData(s => s.session?.org.id);
  const people = useData(s => s.people);
  const navigate = useNavigate();
  const [reports, setReports] = useState<NewsReport[]>([]);
  const [leaving, setLeaving] = useState(false);
  /** Таймер зникнення: новий рапорт, що прийшов під час анімації, його скасовує. */
  const exit = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (!orgId) return;
    let alive = true;
    let busy = false;

    const check = async (): Promise<void> => {
      if (busy || document.visibilityState !== 'visible') return;
      busy = true;
      try {
        const res = await api.get<{ reports: NewsReport[]; until: string }>('/api/leave-reports/news');
        if (!alive) return;
        if (res.reports.length) {
          if (exit.current) clearTimeout(exit.current);
          exit.current = null;
          setLeaving(false);
          setReports(prev => {
            const fresh = new Set(res.reports.map(keyOf));
            return [...prev.filter(r => !fresh.has(keyOf(r))), ...res.reports];
          });
        }
        // Показали — отже, бачив: удруге той самий рапорт не з'явиться.
        if (res.reports.length) await api.post('/api/leave-reports/news/seen', { until: res.until });
      } catch {
        // Не вийшло — спитаємо наступного разу, заважати роботі нема чим.
      } finally {
        busy = false;
      }
    };

    void check();
    const timer = setInterval(() => void check(), POLL_MS);
    const onVisible = (): void => void check();
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      alive = false;
      clearInterval(timer);
      if (exit.current) clearTimeout(exit.current);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [orgId]);

  if (!reports.length) return null;

  const nameOf = (id: string): string => {
    const p = people.find(x => x.id === id);
    return p ? shortName(p) : 'Невідома людина';
  };
  const close = (): void => {
    if (leaving) return;
    setLeaving(true);
    exit.current = setTimeout(() => {
      exit.current = null;
      setReports([]);
      setLeaving(false);
    }, EXIT_MS);
  };
  const open = (): void => {
    close();
    // На сторінку «Рапорти», у пункт «Рапорти на звільнення».
    navigate('/reports', { state: { doc: 'leaveUpcoming' } });
  };

  return (
    <section className={leaving ? 'toast bot-news leaving' : 'toast bot-news'} role="status" aria-live="polite">
      <div className="bot-news-body">
        <h3>Через бота — {reportsText(reports.length)}</h3>
        <ul>
          {reports.slice(-SHOWN).map(r => (
            <li key={keyOf(r)}>
              <span className="t">{nameOf(r.personId)}</span>
              <span className="muted">
                {dayMonth(r.date)}, {r.kind === 'duty' ? 'до ранку' : 'звільнення'}
                {r.changed ? ', змінено' : ''}
              </span>
            </li>
          ))}
        </ul>
        {reports.length > SHOWN ? <p className="muted">і ще {reports.length - SHOWN}</p> : null}
        <button type="button" className="btn sm" onClick={open}>
          Переглянути
        </button>
      </div>
      <button type="button" className="toast-close" aria-label="Закрити" onClick={close}>
        <CloseIcon />
      </button>
    </section>
  );
}
